import { clsx } from "clsx";
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes, type ReactNode, useEffect, useRef } from "react";
import { Loader2, X } from "lucide-react";

// ---------- Button ----------
type BotonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: "primario" | "secundario" | "fantasma" | "peligro";
  tamano?: "md" | "lg" | "sm";
  cargando?: boolean;
};
export const Boton = forwardRef<HTMLButtonElement, BotonProps>(function Boton(
  { variante = "primario", tamano = "md", cargando, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || cargando}
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors select-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        tamano === "sm" && "h-9 px-3 text-sm",
        tamano === "md" && "h-11 px-4",
        tamano === "lg" && "h-14 px-6 text-lg",
        variante === "primario" && "bg-primary text-white hover:bg-primary-hover",
        variante === "secundario" && "bg-white text-tinta border border-borde hover:bg-gray-50",
        variante === "fantasma" && "bg-transparent text-tinta hover:bg-gray-100",
        variante === "peligro" && "bg-error text-white hover:bg-red-600",
        className,
      )}
      {...rest}
    >
      {cargando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

// ---------- Campo (label + control + ayuda/error) ----------
export function Campo({ etiqueta, ayuda, error, children, htmlFor }: { etiqueta: string; ayuda?: string; error?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-tinta">{etiqueta}</label>
      {children}
      {error ? <p className="text-sm text-error" role="alert">{error}</p> : ayuda ? <p className="text-sm text-tinta-suave">{ayuda}</p> : null}
    </div>
  );
}

const claseControl = "h-11 w-full rounded-md border border-borde bg-white px-3 text-base text-tinta placeholder:text-gray-400 focus:border-primary disabled:bg-gray-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={clsx(claseControl, className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return <select ref={ref} className={clsx(claseControl, "pr-8", className)} {...rest}>{children}</select>;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={clsx("min-h-24 w-full rounded-md border border-borde bg-white p-3 text-base focus:border-primary", className)} {...rest} />;
});

// ---------- Badge de estado ----------
export function Badge({ tono = "neutro", children, className }: { tono?: "ok" | "alerta" | "error" | "neutro" | "info"; children: ReactNode; className?: string }) {
  return (
    <span className={clsx(
      "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
      tono === "ok" && "bg-emerald-50 text-emerald-800",
      tono === "alerta" && "bg-alerta-suave text-amber-900",
      tono === "error" && "bg-error-suave text-red-800",
      tono === "neutro" && "bg-gray-100 text-gray-700",
      tono === "info" && "bg-indigo-50 text-indigo-800",
      className,
    )}>{children}</span>
  );
}

export function tonoEstado(estado: string | null | undefined): "ok" | "alerta" | "error" | "neutro" {
  switch (estado) {
    case "agotado": return "error";
    case "bajo": return "alerta";
    case "sin_minimo": return "neutro";
    default: return "ok";
  }
}

// ---------- Tarjeta ----------
export function Tarjeta({ children, className, titulo, accion }: { children: ReactNode; className?: string; titulo?: string; accion?: ReactNode }) {
  return (
    <section className={clsx("rounded-md border border-borde bg-white", className)}>
      {(titulo || accion) && (
        <header className="flex items-center justify-between gap-3 border-b border-borde px-4 py-3">
          {titulo && <h2 className="text-base font-semibold">{titulo}</h2>}
          {accion}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

// ---------- Estados ----------
export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-tinta-suave" role="status" aria-live="polite">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> {texto}
    </div>
  );
}

export function Esqueleto({ filas = 4 }: { filas?: number }) {
  return (
    <div className="animate-pulse space-y-3 p-4" aria-hidden>
      {Array.from({ length: filas }).map((_, i) => <div key={i} className="h-10 rounded bg-gray-100" />)}
    </div>
  );
}

export function Vacio({ titulo, texto, accion, icono }: { titulo: string; texto?: string; accion?: ReactNode; icono?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      {icono && <div className="text-tinta-suave">{icono}</div>}
      <h3 className="text-lg font-semibold">{titulo}</h3>
      {texto && <p className="max-w-md text-tinta-suave">{texto}</p>}
      {accion}
    </div>
  );
}

export function ErrorBloque({ mensaje, reintentar }: { mensaje: string; reintentar?: () => void }) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-error-suave p-4 text-red-900" role="alert">
      <p>{mensaje}</p>
      {reintentar && <Boton variante="secundario" tamano="sm" onClick={reintentar} className="self-start">Reintentar</Boton>}
    </div>
  );
}

// ---------- Diálogo (nativo) ----------
export function Dialogo({ abierto, onCerrar, titulo, children, ancho = "md" }: { abierto: boolean; onCerrar: () => void; titulo: string; children: ReactNode; ancho?: "md" | "lg" }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);
  return (
    <dialog
      ref={ref}
      onClose={onCerrar}
      onClick={(e) => { if (e.target === ref.current) onCerrar(); }}
      className={clsx("m-auto w-[calc(100%-2rem)] rounded-lg border border-borde bg-white p-0 shadow-xl backdrop:bg-black/40", ancho === "md" ? "max-w-lg" : "max-w-3xl")}
    >
      <div className="flex items-center justify-between border-b border-borde px-5 py-3">
        <h2 className="text-lg font-semibold">{titulo}</h2>
        <button onClick={onCerrar} aria-label="Cerrar" className="rounded p-1 hover:bg-gray-100"><X className="h-5 w-5" /></button>
      </div>
      <div className="p-5">{children}</div>
    </dialog>
  );
}
